import { useEffect, useRef, useState } from 'react';
import { wsBackend } from '../constants/api';
import { get } from '../services/back.service';

const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 60000;
// Código que el backend envía cuando el peritaje no existe: no se reintenta.
const CLOSE_NOT_FOUND = 4404;
// Código que el backend envía cuando el token no vale: no se reintenta.
const CLOSE_UNAUTHORIZED = 4401;
const POLL_MS = 10000;

/**
 * Conexión en tiempo real al progreso de peritajes (WP4).
 *
 * Uso:
 *   const { connected } = useProgressSocket({ office, inspectionId, onMessage });
 *
 * - Conecta a ws/progresslist/ (con filtro opcional por oficina) o a
 *   ws/inspection/<id>/ cuando se pasa inspectionId.
 * - onMessage recibe el payload ({ type: 'progresslist' | 'progress', data }).
 * - Si el socket no está disponible, consulta por REST (polling) para que la
 *   pantalla nunca quede vacía, y reconecta solo.
 */
export default function useProgressSocket({ office, inspectionId, onMessage } = {}) {
  const [connected, setConnected] = useState(false);
  const onMessageRef = useRef(onMessage);

  useEffect(() => {
    onMessageRef.current = onMessage;
  });

  useEffect(() => {
    const isSingle = inspectionId !== undefined && inspectionId !== null;
    const id = Number(inspectionId);
    // Un id inválido (p. ej. "0") no abre socket ni polling.
    if (isSingle && (!Number.isInteger(id) || id <= 0)) return undefined;

    let socket = null;
    let stopped = false;
    let reconnectTimer = null;
    let pollTimer = null;
    let reconnectAttempts = 0;

    const token = localStorage.getItem('token') || '';
    const url = isSingle
      ? `${wsBackend}/ws/inspection/${id}/?token=${encodeURIComponent(token)}`
      : `${wsBackend}/ws/progresslist/?token=${encodeURIComponent(token)}${office ? `&office=${office}` : ''}`;

    const nextReconnectDelay = () => {
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempts);
      reconnectAttempts += 1;
      return delay;
    };

    const deliver = (payload) => {
      if (onMessageRef.current) onMessageRef.current(payload);
    };

    const poll = async () => {
      const path = isSingle
        ? `/peritaje/progress/${id}`
        : '/peritaje/progresslist/';
      const params = isSingle ? {} : { office };
      const respo = await get(path, {}, params);
      if (stopped || !respo || !respo.success) return;
      deliver({
        type: isSingle ? 'progress' : 'progresslist',
        data: respo.data,
      });
    };

    const startPolling = () => {
      if (stopped || pollTimer) return;
      pollTimer = setInterval(poll, POLL_MS);
    };

    const connect = () => {
      if (stopped) return;
      let down = false;
      socket = new WebSocket(url);

      socket.onopen = () => {
        reconnectAttempts = 0;
        setConnected(true);
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      };

      socket.onmessage = (event) => {
        try {
          deliver(JSON.parse(event.data));
        } catch (e) {
          // payload no-JSON: se ignora
        }
      };

      const handleDown = (event) => {
        if (down || stopped) return;
        down = true;
        setConnected(false);
        socket = null;
        if (
          event &&
          (event.code === CLOSE_NOT_FOUND || event.code === CLOSE_UNAUTHORIZED)
        ) {
          stopped = true;
          if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
          }
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          return;
        }
        startPolling();
        reconnectTimer = setTimeout(connect, nextReconnectDelay());
      };

      socket.onclose = handleDown;
      socket.onerror = handleDown;
    };

    startPolling();
    connect();

    return () => {
      stopped = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (pollTimer) clearInterval(pollTimer);
      if (socket) socket.close();
    };
  }, [office, inspectionId]);

  return { connected };
}
